import { useState, useCallback } from 'react';
import { createApiClient, ApiError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 库存模块端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type ConsumableResponseDto = ApiComponents['schemas']['ConsumableResponseDto'];
export type CreateConsumableDto = ApiComponents['schemas']['CreateConsumableDto'];
export type StockInDto = ApiComponents['schemas']['StockInDto'];
export type StockOutDto = ApiComponents['schemas']['StockOutDto'];
export type ReserveStockDto = ApiComponents['schemas']['ReserveStockDto'];

interface ApiErrorResponse {
  message?: string;
}

export interface InventoryApiError {
  message: string;
  code?: string;
  status?: number;
}

function thrownToInventoryError(err: unknown, fallback: string): InventoryApiError {
  if (err instanceof ApiError) {
    if (err.status === 401) return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED', status: 401 };
    if (err.status === 403) return { message: '当前角色没有库存模块写入权限', code: 'FORBIDDEN', status: 403 };
    if (err.status === 409) return { message: '存在数据冲突（409），请刷新后再次提交', code: 'CONFLICT', status: 409 };
    if (err.status === 400) return { message: err.message || '提交内容不符合要求', code: 'BAD_REQUEST', status: 400 };
    return { message: err.message, code: err.code, status: err.status };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: fallback };
}

function friendlyMessage(err: unknown, fallback: string): string {
  return thrownToInventoryError(err, fallback).message;
}

function responseErrToInventoryError(apiErr: unknown, fallback: string): InventoryApiError {
  if (apiErr === null || apiErr === undefined) return { message: fallback };
  if (typeof apiErr === 'object') {
    const e = apiErr as Record<string, unknown>;
    const message =
      typeof e['message'] === 'string'
        ? e['message']
        : typeof e['error'] === 'string'
          ? (e['error'] as string)
          : fallback;
    const code = typeof e['code'] === 'string' ? (e['code'] as string) : undefined;
    const status = typeof e['status'] === 'number' ? (e['status'] as number) : undefined;
    return { message, code, status };
  }
  return { message: fallback };
}

export function useInventory() {
  const [loading, setLoading] = useState(false);

  const fetchConsumables = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await apiClient.GET('/api/v1/inventory/consumables');
      if (error) {
        const err = error as ApiErrorResponse;
        throw new Error(err.message || 'Failed to fetch consumables');
      }
      return data;
    } catch (e) {
      throw new Error(friendlyMessage(e, 'Failed to fetch consumables'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMovements = useCallback(async (consumableId: string) => {
    setLoading(true);
    try {
      const { data, error } = await apiClient.GET('/api/v1/inventory/movements', {
        params: {
          query: { consumableId }
        }
      });

      if (error) {
        const err = error as ApiErrorResponse;
        throw new Error(err.message || 'Failed to fetch movements');
      }
      return data;
    } catch (e) {
      throw new Error(friendlyMessage(e, 'Failed to fetch movements'));
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    fetchConsumables,
    fetchMovements,
  };
}

/**
 * useConsumableCreate — POST /inventory/consumables。
 *
 * Issue #238 post-deploy follow-up: the prior implementation rendered `新增耗材`
 * as a static button whose only effect was a controlled "暂未上线" notice, which
 * the production E2E correctly flagged as not fulfilling the acceptance
 * criterion. The backend `InventoryController.create` contract is stable and
 * minimal (`name`/`category`/`specification`/`unit`/`minStock`), so we now
 * expose a real typed create call. The caller refreshes the list from the API
 * after a successful POST — there is no frontend-only optimistic state.
 */
export function useConsumableCreate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<InventoryApiError | null>(null);

  const create = useCallback(
    async (body: CreateConsumableDto): Promise<Readonly<ConsumableResponseDto> | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr } = await apiClient.POST('/api/v1/inventory/consumables', { body });
        if (apiErr) {
          setError(responseErrToInventoryError(apiErr, '创建耗材失败'));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(thrownToInventoryError(err, '创建耗材失败'));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => setError(null), []);

  return { loading, error, create, reset };
}

/**
 * useConsumableStockIn — POST /inventory/consumables/{id}/stock-in。
 *
 * Issue #345：将 `入库 / 出库流程暂未上线` 占位替换为真实后端调用。后端服务在
 * `InventoryService.stockIn` 内使用事务 + Prisma.Decimal 计算总库存，前端只
 * 把表单值原样透传；调用方在 resolve 后必须从后端重新拉取列表，不维护任何
 * 前端假状态。
 */
export function useConsumableStockIn() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<InventoryApiError | null>(null);

  const stockIn = useCallback(
    async (consumableId: string, body: StockInDto): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/inventory/consumables/{id}/stock-in',
          { params: { path: { id: consumableId } }, body },
        );
        if (apiErr) {
          setError(responseErrToInventoryError(apiErr, '入库失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToInventoryError(err, '入库失败'));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => setError(null), []);
  return { loading, error, stockIn, reset };
}

/**
 * useConsumableStockOut — POST /inventory/consumables/{id}/stock-out。
 *
 * Issue #345。后端服务在 InventoryService.stockOut 内按 batch 锁版本 +
 * 写 immutable inventory_movements；前端不做扣减计算，仅透传数量字符串。
 */
export function useConsumableStockOut() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<InventoryApiError | null>(null);

  const stockOut = useCallback(
    async (consumableId: string, body: StockOutDto): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/inventory/consumables/{id}/stock-out',
          { params: { path: { id: consumableId } }, body },
        );
        if (apiErr) {
          setError(responseErrToInventoryError(apiErr, '出库失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToInventoryError(err, '出库失败'));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => setError(null), []);
  return { loading, error, stockOut, reset };
}

/**
 * useConsumableReserve — POST /inventory/consumables/{id}/reserve。
 *
 * Issue #345。后端服务在 InventoryService.reserveStock 内做 effectiveAvailable
 * 的 Decimal 计算与 RESERVATION pending 写入。
 */
export function useConsumableReserve() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<InventoryApiError | null>(null);

  const reserve = useCallback(
    async (consumableId: string, body: ReserveStockDto): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const { error: apiErr } = await apiClient.POST(
          '/api/v1/inventory/consumables/{id}/reserve',
          { params: { path: { id: consumableId } }, body },
        );
        if (apiErr) {
          setError(responseErrToInventoryError(apiErr, '预留库存失败'));
          return false;
        }
        return true;
      } catch (err) {
        setError(thrownToInventoryError(err, '预留库存失败'));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => setError(null), []);
  return { loading, error, reserve, reset };
}
